import {
  LANGUAGES,
  type Language,
} from '@basehub/mutation-api-helpers/src/languages'
import { railwayURL } from './raw'

export { type Language, LANGUAGES }

export const socialLinks = {
  github: 'https://github.com/basehub-ai/basehub',
  x: 'https://x.com/basehub_ai',
}

export const xGlyph = '𝕏'

export const productionOrigin = 'https://basehub.com'
export const productionAPIOrigin = 'https://api.basehub.com'
export const productionR2Origin = 'https://basehub.earth'
export const productionAssetsURL = 'https://assets.basehub.com'
export const cloudflareImagesDefaultOrigin = 'https://imagedelivery.net'
export const sitesDispatcherDomain = 'basehub.site'

export type Environment = 'development' | 'preview' | 'production'

export const appURL = {
  development: 'http://localhost:3000',
  preview: 'https://basehub-git-jb-pumpdo-new-try-basehub.vercel.app',
  production: 'https://basehub.com',
} satisfies Record<Environment, string>
export const apiURL = {
  development: 'http://localhost:3001',
  preview: 'https://api.bshb.dev',
  production: 'https://api.basehub.com',
} satisfies Record<Environment, string>
export const startWorkerURL = {
  development: 'http://localhost:3002',
  preview: 'https://start.bshb.dev',
  production: 'https://start.basehub.com',
} satisfies Record<Environment, string>
export const sitesWorkerURL = {
  development: 'http://localhost:3003',
  preview: 'https://sites.bshb.dev',
  production: 'https://sites.basehub.com',
} satisfies Record<Environment, string>
export const pumpURL = railwayURL satisfies Record<Environment, string>
export const agentWorkerURL = {
  development: 'http://localhost:5173',
  preview: 'https://agent.bshb.dev',
  production: 'https://agent.basehub.com',
} satisfies Record<Environment, string>
export const mcpURL = {
  development: 'http://localhost:3005',
  // development: 'https://mcp-black.vercel.app',
  preview: 'https://mcp-black.vercel.app',
  production: 'https://mcp-black.vercel.app',
} satisfies Record<Environment, string>

export const urls = {
  app: appURL,
  api: apiURL,
  sitesWorker: sitesWorkerURL,
  startWorker: startWorkerURL,
  pump: pumpURL,
  search: railwayURL,
  agentWorker: agentWorkerURL,
  mcp: mcpURL,
}

export const getURL = (
  selection: keyof typeof urls,
  forceEnv?: Environment
) => {
  let inferredEnv =
    forceEnv ||
    // @ts-ignore
    process?.env?.ENVIRONMENT ||
    // @ts-ignore
    process?.env?.NEXT_PUBLIC_ENVIRONMENT ||
    // @ts-ignore
    process?.env?.VERCEL_ENV ||
    // @ts-ignore
    process?.env?.NEXT_PUBLIC_VERCEL_ENV ||
    // @ts-ignore
    process?.env?.NODE_ENV

  if (inferredEnv) {
    inferredEnv = inferredEnv.trim().toLowerCase() as Environment
  }

  // @ts-ignore
  const sel = urls[selection][inferredEnv]

  if (!sel) {
    throw new Error(`No URL found for ${selection} in env ${inferredEnv}`)
  }

  return sel as string
}

export const PUSHER_KEY = '91d723201cad1ff7449b'
export const PUSHER_CLUSTER = 'mt1'

export const getFooterTagline = () => {
  return `©${new Date().getUTCFullYear()} BaseHub Corp — Fast, Collaborative, AI-Native Content Management.`
}

export const apiDefaultBranchRef = '__default__'

export const LANGUAGES_KEYS = Object.keys(LANGUAGES) as Language[]

/**
 * @see https://developers.cloudflare.com/images/transform-images/transform-via-workers/#fetch-options
 */
export const VALID_IMAGE_RESIZING_PARAMS = [
  'fit',
  'width',
  'height',
  'quality',
  'format',
  'anim',
  'background',
  'blur',
  'border',
  'brightness',
  'compression',
  'contrast',
  'dpr',
  'gamma',
  'gravity',
  'metadata',
  'onerror',
  'rotate',
  'sharpen',
  'trim',
  'w',
  'h',
  'f',
  'q',
]

export type Emoji = {
  name: string
  emoji: string
  emojiAlt?: string
  hideFromPicker?: boolean
}

export const emojis: Emoji[] = [
  { name: 'Thumbs Up', emoji: '👍', emojiAlt: ':+1:' },
  { name: 'Thumbs Down', emoji: '👎', emojiAlt: ':-1:' },
  { name: 'Heart', emoji: '🧡', emojiAlt: ':heart:' },
  { name: 'Checked', emoji: '✅', emojiAlt: ':checked:' },
  { name: 'Party', emoji: '🎉', emojiAlt: ':tada:' },
  { name: 'Rocket', emoji: '🚀', emojiAlt: ':rocket:' },
  { name: 'Folded Hands', emoji: '🙏', emojiAlt: ':folded_hands:' },
  { hideFromPicker: true, name: 'Refresh', emoji: '🔄', emojiAlt: ':refresh:' },
  { hideFromPicker: true, name: 'Clap', emoji: '👏', emojiAlt: ':clap:' },
  {
    hideFromPicker: true,
    name: 'Laughing',
    emoji: '😂',
    emojiAlt: ':laughing:',
  },
  { hideFromPicker: true, name: 'Salute', emoji: '🫡', emojiAlt: ':salute:' },
]

export type SystemAuthor = {
  id: string
  name: string
  iconUrl: string
  createdAt: string
  bio?: string
}

const CREATED_AT_TIMESTAMP = '2024-06-20T00:00:00.044Z'

export const SYSTEM_AUTHORS = {
  API_TOKEN: {
    id: 'api-token',
    name: 'API Token',
    iconUrl: '/_static/system-authors/api-token.svg',
    createdAt: CREATED_AT_TIMESTAMP,
  },
  GHOST: {
    id: 'ghost',
    name: 'Deleted User',
    iconUrl: '/_static/system-authors/ghost.svg',
    createdAt: CREATED_AT_TIMESTAMP,
    bio: 'This user has been deleted',
  },
  UNKNOWN: {
    id: 'unknown',
    name: 'Unknown User',
    iconUrl: '/_static/system-authors/ghost.svg',
    createdAt: CREATED_AT_TIMESTAMP,
    bio: 'This is a non-logged user',
  },
  START: {
    id: 'START',
    name: 'START',
    iconUrl: '/_static/system-authors/START.svg',
    createdAt: '2024-11-15T15:23:09.684Z',
    bio: `Hi, I'll help you generate content and build websites.`,
  },
} satisfies Record<string, SystemAuthor>

export const clientStaleTimeMs = 30_000
export const clientStaleTimeS = clientStaleTimeMs / 1_000
export const clientPingIntervalMs = 25_000

export const isClient = typeof window !== 'undefined'
export const isServer = !isClient
// @ts-ignore
export const canAccessProcessEnv = typeof process !== 'undefined'
export const isDev =
  // @ts-ignore
  canAccessProcessEnv && process.env.NODE_ENV === 'development'
export const isProd =
  // @ts-ignore
  canAccessProcessEnv && process.env.NODE_ENV === 'production'

/**
 * 5 days
 */
export const PLAYGROUND_EXPIRATION_TIME_MS = 5 * 24 * 60 * 60 * 1000

/**
 * 5 minutes
 */
export const WORKFLOW_AUTOFILL_TIME_MS = 5 * 60 * 1000

export const SUPPORTED_LLM_FILE_TYPES = [
  'application/pdf',
  'text/csv',
  'text/plain',
  'text/markdown',
  'text/html',
  'text/xml',
  'text/css',
  'text/javascript',
  'text/json',
] as const

export const JS_MAX_TIMEOUT_MS = 2147483647
export const API_TOKEN_PREFIX = 'bshb_pk_'
